import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, take, tap, switchMap, catchError, mergeMap, finalize } from 'rxjs/operators';
import { Order } from 'src/app/shared/models/orders';
import * as moment from 'moment';
import { RejectReason } from 'src/app/shared/models/RejectReason';
import { Observable, of, from, throwError } from 'rxjs';
import { ExtraField, OfflineStorageService } from 'src/app/services/offline/offline-storage.service';
import { OrdersListRefreshService } from 'src/app/services/offline/orders-list-refresh.service';
import { TransportistaDatosUsuarioGatewayApi } from 'src/core/apis/datos-usuario';
import { TransportistaViajesGatewayApi, toLegacyViajesEnvelope } from 'src/core/apis/viajes.list';
import { TransportistaViajesSetValuesGatewayApi } from 'src/core/apis/viajes.set-values';
import {
  mapOrdersFromApi,
  orderIsSignedFromApi,
  transformViajeDate,
} from 'src/app/services/pedidos/viajes-order.mapper';
import { TransportistaViajesClienteSetValuesGatewayApi } from 'src/core/apis/viajes.cliente.set-values';
import { TransportistaMotivosRechazoGatewayApi } from 'src/core/apis/motivos.rechazo';
import { TransportistaMotivosRetornoGatewayApi } from 'src/core/apis/motivos.retorno';
import { TransportistaEstadoSetGatewayApi } from 'src/core/apis/estado.set';
import { TransportistaDespachosResumenGatewayApi } from 'src/core/apis/despachos.resumen';
import { TransportistaSessionStorage } from 'src/core/jwt/session.storage';
import { TransportistaGatewayPostOptions } from 'src/core/interceptor/http';
import {
  buildSetValuesGatewayFormData,
  omitLegacyKeyFromSetValuesHeaders,
  normalizeMotivosRetornoToLegacyRows,
  normalizeGatewayRejectReasonsGrouped,
  SetValuesFileAttachment,
} from 'src/app/services/pedidos/gateway-pedidos.mapper';
import { toLegacyResumenDespachoArray } from 'src/app/services/pedidos/gateway-consultas.mapper';
import { ViajesFirmaTipo } from 'src/core/apis/viajes.firma';
import { ViajesFirmaService } from 'src/app/services/firma-digital/viajes-firma.service';
import { EventLogService } from 'src/app/shared/logging/event-log.service';
import { CorrelationContextService } from 'src/core/logging/correlation-context.service';
import { EVENT_LOG_ENDPOINTS } from 'src/core/logging/event-log-endpoints';
import {
  httpErrorContext,
  httpSuccessContext,
  mergeHttpLogPayload,
} from 'src/core/logging/http-event-log-context';
import { environment } from 'src/environments/environment';

/** Ventana en la que un refresh de listado respeta parches locales de firma/pendiente. */
const RECENT_MUTATION_MS = 5 * 60 * 1000;

const EXTRA_FIELD_HEADER: Record<ExtraField, string> = {
  waterAddition: 'MX_ADICIONAL_UNO_H',
  anotherAddition: 'MX_ADICIONAL_DOS_H',
  guidingRemarks: 'MX_ADICIONAL_TRES_H',
  m3Returned: 'MX_M3_RETORNO',
};

@Injectable({
  providedIn: 'root',
})
export class OrderService {
  private key: string;
  private readonly recentlyMutated = new Map<string, number>();

  constructor(
    private http: HttpClient,
    private offlineStorage: OfflineStorageService,
    private readonly viajesListGateway: TransportistaViajesGatewayApi,
    private readonly datosUsuarioGatewayApi: TransportistaDatosUsuarioGatewayApi,
    private readonly viajesSetValuesGateway: TransportistaViajesSetValuesGatewayApi,
    private readonly viajesClienteSetValuesGateway: TransportistaViajesClienteSetValuesGatewayApi,
    private readonly motivosRechazoGateway: TransportistaMotivosRechazoGatewayApi,
    private readonly motivosRetornoGateway: TransportistaMotivosRetornoGatewayApi,
    private readonly estadoSetGateway: TransportistaEstadoSetGatewayApi,
    private readonly despachosResumenGateway: TransportistaDespachosResumenGatewayApi,
    private readonly gatewaySession: TransportistaSessionStorage,
    private readonly ordersListRefresh: OrdersListRefreshService,
    private readonly viajesFirmaService: ViajesFirmaService,
    private readonly eventLog: EventLogService,
    private readonly correlationContext: CorrelationContextService
  ) {
    // Rehydrate auth key from the persisted session on service construction.
    //
    // setValues() is otherwise only called by the login form and the auth guard,
    // which means after a fresh app launch (kill/reopen, page refresh) `this.key`
    // is undefined until the user navigates to a guarded route. SyncService.init()
    // fires its first flush before any navigation, so every queued send goes out
    // with `Key: undefined` → server rejects → entry stuck. Reading from the same
    // localStorage entry the auth flow writes to closes that gap.
    this.rehydrateKeyFromStorage();
  }

  private gatewayOrderBackgroundHeaders(): HttpHeaders {
    return new HttpHeaders()
      .set('X-Skip-Loader', '1')
      .set('X-Background-Request', '1');
  }

  private assertSetValuesIdentifiers(
    tkt: string | undefined,
    orderNumber: string | undefined,
    operation: string
  ): void {
    if (!tkt?.trim() || !orderNumber?.trim()) {
      console.error(
        `[OrderService] ${operation}: tkt y orderNumber son obligatorios para set-values`,
        { tkt, orderNumber }
      );
      throw new Error(
        `${operation}: tkt y orderNumber son obligatorios para set-values`
      );
    }
  }

  private applySetValuesGateway(
    legacyHeaders: Record<string, string>,
    files: File[] | undefined,
    options?: TransportistaGatewayPostOptions,
    cliente = false,
    queuedFiles: SetValuesFileAttachment[] = []
  ): Observable<any> {
    const api = cliente
      ? this.viajesClienteSetValuesGateway
      : this.viajesSetValuesGateway;
    const headersForGateway = omitLegacyKeyFromSetValuesHeaders(legacyHeaders);
    return from(
      buildSetValuesGatewayFormData(
        headersForGateway,
        files ?? [],
        queuedFiles
      )
    ).pipe(
      switchMap((body) => api.post(body, options)),
      tap({
        next: () => this.ordersListRefresh.requestListRefresh(),
        error: (err) =>
          console.error('[OrderService] POST /viajes/set-values error', err),
      })
    );
  }

  private rehydrateKeyFromStorage(): void {
    try {
      const user = JSON.parse(localStorage.getItem('user') ?? 'null');
      if (user?.KEY) this.key = String(user.KEY);
    } catch { /* malformed user record — leave key undefined, login flow will set it */ }
  }

  private transformDate(inputDate: string): string {
    return transformViajeDate(inputDate);
  }

  setValues(key: string) {
    this.key = key;
  }

  /** True when hay credencial para operaciones de pedidos — SyncService usa esto antes del flush. */
  hasAuthKey(): boolean {
    if (environment.useGatewayAuth) {
      return this.hasGatewayToken() && !this.gatewaySession.isTokenExpired();
    }
    return !!this.gatewaySession.getToken() || !!this.key;
  }

  /** Bearer JWT requerido por POST /viajes/firma y demás rutas del API Gateway. */
  hasGatewayToken(): boolean {
    return !!this.gatewaySession.getToken();
  }

  setStatus(status: string, rut: string) {
    const body = {
      m_estado_id: status.toString(),
      m_rut_estado: rut,
    };
    return this.estadoSetGateway.post(body).pipe(map((data) => data));
  }

  getMockOrders() {
    return this.http.get<any>('./assets/mock/orders.json').pipe(
      map((data: any) => {
        return data.datos[0].VIAJESH.map((item) => {
          return <Order>{
            tkt: item.TKT_H,
            deliveryDate: String(item.FECHA_PED_DATE_H ?? ''),
            orderDate: this.transformDate(item.FECHA_PED_H),
            plant: item.PLANTA_H,
            orderNumber: item.NUMERO_PEDID_H,
            clientCode: item.CODIGO_CLI_H,
            clientName: item.NOMBRE_CLIENTE_H,
            workCode: item.CODIGO_PROYE_H,
            workName: item.NOMBRE_PROYECTO_H,
            deliveryAddress: item.DIRECCION_ENTREGA_H,
            productCode: item.CODIGO_PRODU_H,
            productDescription: item.DESCRIPCION_PRODUCTO_H,
            scheduledAmount: item.CANTIDAD_PROGRAMADA_H,
            loadAmount: item.CARGAS_H,
            frequency: item.FRECUENCIA_H,
            ticketPrintingTime: item.IMPRESO_H,
            startTime: item.HORA_INI_H,
            estimatedTravelTime: item.TIEMPO_DE_VIAJE_H,
            requiredTrucks: item.CAMIONES_REQUERIDOS_H,
            loadSize: item.TAMANO_CARGA_H,
            quantityShipped: item.CANTIDAD_DESPACHADA_H,
            outstandingAmount: item.CANTIDAD_PENDIENTE_H,
            OnsiteContactPhoneNumber: item.TELEFONO_H,
            OnsiteContactName: item.CONTACTO_H,
            truckNumber: item.DESIGNATION_H,
            driverName: item.CONDUCTOR_H,
            truckPatent: item.PATENTE_H,
            currentTripNumber: item.NROCARGA_H,
            status: +item.STATUS_H,
            webOrderNumber: item.ORDL_H,
            hourOne: item.HORA_UNO_H,
            hourTwo: item.HORA_DOS_H,
            hourThree: item.HORA_TRES_H,
            hourFour: item.HORA_CUATRO_H,
            hourFive: item.HORA_CINCO_H,
            hourSix: item.HORA_SEIS_H,
            hourSeven: item.HORA_SIETE_H,
            hourEight: item.HORA_OCHO_H,
            folioNumberGD: item.FOLIO_H,
            stateDescription: item.ESTADO,
            rejectionFlag: item.MX_MOTIVO_RECHAZO,
            isSignature: this.orderIsSignedFromApi(item),
            flag_pending:
              item.FLAG_PEDIDO_PEN === '1' || item.MX_PENDIENTE_H === '1'
                ? '1'
                : '0',
            concreteReturn: item.MX_COMENTARIOS_HORMI,
            waterAddition:  (item.MX_ADICIONAL_UNO_H === "" || item.MX_ADICIONAL_UNO_H === "false") ? false : (item.MX_ADICIONAL_UNO_H === "true" ? true : undefined),  //item.MX_ADICIONAL_UNO_H != '',  
            anotherAddition: (item.MX_ADICIONAL_DOS_H === "" || item.MX_ADICIONAL_DOS_H === "false") ? false : (item.MX_ADICIONAL_DOS_H === "true" ? true : undefined),
            routePdfGuide: item.PDF_GUIA_H,
            instructions: item.INSTRUCCIONES,
            additiveRemarks: item.OBSERVACIONES_ADIT,
            sealNumber: item.SELLO,
            cone: item.CONO,
            reasonToReturn: item.MX_MOTIVO_RETORNO,
            m3Returned: item.MX_M3_RETORNO !== "" ? item.MX_M3_RETORNO : "",
            samples: item.MUESTRAS,
            loadingMouth: item.BOCA_CARGA,
            urlMap: item.URLMAP,
            files: item.ARCHIVOS.map((file) => {
              return {
                id: file.MX_FILES_ID,
                fileName: file.MX_NOMBRE_ARCHIVO,
                label: file.MX_ETIQUETA_ARCHIVO,
                url: file.URL,
              };
            }),
            operatorComments: item.COMENTARIOS_OPERADORES.map((comment) => {
              return {
                id: comment.MX_COMENTARIO_ID,
                tkt: comment.MX_TKT_H_COM,
                rut: comment.MX_RUT_CONDUCTOR,
                driverName: comment.MX_NOMBRE_CONDUCTOR,
                workCode: comment.MX_CODIGO_PROYE_H,
                date: comment.MX_FECHA_COMENTARIO,
                comment: comment.MX_COMENTARIO,
              };
            }),
          };
        });
      })
    );
  }

 async getAllOrders(forceNetwork = false) {
    if (!this.offlineStorage.isOnline()) {
      const cached = await this.offlineStorage.getOrders();
      return of(cached ?? []);
    }

    if (!forceNetwork) {
      const cached = await this.offlineStorage.getOrders();
      if (cached && cached.length > 0) {
        return of(cached);
      }
    }

    return this.fetchOrdersListFromDatosUsuario(!forceNetwork).pipe(
      switchMap((orders) => from(this.mergeOrdersForCache(orders))),
      tap((orders) => this.offlineStorage.saveOrders(orders).catch(() => null))
    );
  }

  /** Always fetches fresh orders from the API. Silent (no global spinner). Used by CacheWarmupService only. */
  fetchOrdersDirect(): Observable<Order[]> {
    const correlationId = this.correlationContext.startFlow();
    this.eventLog.log('viajes', 'cache_warmup_iniciado', { correlationId });
    return this.fetchOrdersListFromDatosUsuario(true).pipe(
      tap((orders) => {
        this.eventLog.log(
          'viajes',
          'cache_warmup_completado',
          mergeHttpLogPayload(
            {
              count: orders?.length ?? 0,
              correlationId,
            },
            httpSuccessContext(EVENT_LOG_ENDPOINTS.AUTH_DATOS_USUARIO)
          )
        );
      }),
      finalize(() => this.correlationContext.endFlow())
    );
  }

  /**
   * Fusiona listado API con cola offline y parches locales recientes (firma, pendiente).
   * Usado por CacheWarmupService al persistir en IndexedDB.
   */
  async mergeOrdersForCache(orders: Order[]): Promise<Order[]> {
    const queue = await this.offlineStorage.getQueue();
    const queuedTkts = new Set(queue.map((e) => e.tkt.toString()));
    const cachedOrders = (await this.offlineStorage.getOrders()) || [];
    const cachedMap = new Map(cachedOrders.map((o) => [o.tkt.toString(), o]));

    return orders.map((o) => {
      const tkt = o.tkt.toString();
      if (queuedTkts.has(tkt) && cachedMap.has(tkt)) {
        return cachedMap.get(tkt)!;
      }
      if (this.isRecentlyMutated(tkt) && cachedMap.has(tkt)) {
        return this.mergeOrderWithLocalPriority(o, cachedMap.get(tkt)!);
      }
      return o;
    });
  }

  /**
   * Listado de viajes vía POST /datos-usuario (VIAJESH en datos[0]).
   */
  private fetchOrdersListFromDatosUsuario(background = true): Observable<Order[]> {
    const options = background
      ? { headers: this.gatewayOrderBackgroundHeaders() }
      : undefined;
    return this.datosUsuarioGatewayApi.fetch(options).pipe(
      tap((raw) => this.syncViajesSnapshotFromGateway(raw)),
      map((raw) => this.mapOrdersFromApi(raw)),
      catchError((err) => {
        const httpCtx =
          err instanceof HttpErrorResponse
            ? httpErrorContext(err, EVENT_LOG_ENDPOINTS.AUTH_DATOS_USUARIO)
            : {
                endpointOrigen: EVENT_LOG_ENDPOINTS.AUTH_DATOS_USUARIO,
                codigoHttp: err?.status,
              };
        this.eventLog.log('viajes', 'error', { ...httpCtx });
        console.warn('[OrderService] POST /datos-usuario list failed', err);
        return this.fallbackOnListFetchError();
      })
    );
  }

  /** Con sesión activa: conservar IndexedDB; sin token: snapshot de login solo como último recurso. */
  private fallbackOnListFetchError(): Observable<Order[]> {
    if (this.gatewaySession.getToken()) {
      return from(this.offlineStorage.getOrders()).pipe(
        map((cached) => cached ?? [])
      );
    }
    return this.fallbackOrdersFromLoginSnapshot();
  }

  /** Actualiza localStorage.user.VIAJES_M tras cada /datos-usuario exitoso. */
  private syncViajesSnapshotFromGateway(raw: unknown): void {
    try {
      const r = raw as { datos?: Array<{ VIAJESH?: unknown[] }> };
      const viajes = r?.datos?.[0]?.VIAJESH;
      if (!Array.isArray(viajes)) {
        return;
      }
      const userRaw = localStorage.getItem('user');
      if (!userRaw) {
        return;
      }
      const user = JSON.parse(userRaw);
      user.VIAJES_M = viajes;
      localStorage.setItem('user', JSON.stringify(user));
    } catch {
      /* ignore malformed user / response */
    }
  }

  private markRecentlyMutated(tkt: string): void {
    this.recentlyMutated.set(tkt.toString(), Date.now());
  }

  private isRecentlyMutated(tkt: string): boolean {
    const at = this.recentlyMutated.get(tkt.toString());
    if (!at) {
      return false;
    }
    if (Date.now() - at > RECENT_MUTATION_MS) {
      this.recentlyMutated.delete(tkt.toString());
      return false;
    }
    return true;
  }

  clearRecentMutations(): void {
    this.recentlyMutated.clear();
  }

  /** Firma desde campos legacy del API (VIAJESH). */
  private orderIsSignedFromApi(item: {
    MX_CLI_FIRMA?: string;
    MX_CLIENTE_FIRMA_H?: string;
  }): boolean {
    return orderIsSignedFromApi(item);
  }

  private mergeOrderWithLocalPriority(api: Order, local: Order): Order {
    const merged = { ...api };
    if (local.isSignature) {
      merged.isSignature = true;
      merged.isNotSigned = false;
      if (local.flag_pending !== '1') {
        merged.flag_pending = '0';
      }
    }
    if (local.isNotSigned) {
      merged.isNotSigned = true;
      merged.isSignature = false;
    }
    if (local.flag_pending === '1') {
      merged.flag_pending = '1';
    } else if (local.flag_pending === '0') {
      merged.flag_pending = '0';
    }
    if (local.rejectionFlag) {
      merged.rejectionFlag = local.rejectionFlag;
    }
    const localManual = local.manualStatus ?? 0;
    const apiManual = merged.manualStatus ?? 0;
    merged.manualStatus = Math.max(apiManual, localManual);
    return merged;
  }

  /**
   * Listado vivo de viajes (Fase 2). Fuente: POST /viajes con body {}.
   * No usar localStorage.VIAJES_M del login (/datos-usuario) para el listado.
   */
  private fetchViajesListFromGateway(background = true): Observable<Order[]> {
    const options = background
      ? { headers: this.gatewayOrderBackgroundHeaders() }
      : undefined;
    return this.viajesListGateway.post({}, options).pipe(
      map((raw) => this.mapOrdersFromApi(toLegacyViajesEnvelope(raw))),
      catchError((err) => {
        console.warn('[OrderService] POST /viajes list failed', err);
        return this.fallbackOrdersFromLoginSnapshot();
      })
    );
  }

  /** Fallback si POST /viajes falla: snapshot VIAJESH del login (solo rescate de red). */
  private fallbackOrdersFromLoginSnapshot(): Observable<Order[]> {
    try {
      const user = JSON.parse(localStorage.getItem('user') ?? 'null');
      const viajes = user?.VIAJES_M ?? [];
      if (viajes.length > 0) {
        return of(
          this.mapOrdersFromApi({ datos: [{ VIAJESH: viajes }] })
        );
      }
    } catch {
      /* ignore malformed user */
    }
    return of([]);
  }

  private mapOrdersFromApi(data: any): Order[] {
    return mapOrdersFromApi(data);
  }

  getOrder(tkt: string, background = false, forceNetwork = false): Observable<Order[]> {
    return from(this.resolveOrder(tkt, background, forceNetwork));
  }

  private async resolveOrder(
    tkt: string,
    background = false,
    forceNetwork = false,
  ): Promise<Order[]> {
    const online = this.offlineStorage.isOnline();

    // 1. Detail cache — works both online and offline.
    //    forceNetwork solo salta la caché cuando hay conexión.
    if (!(forceNetwork && online)) {
      const detail = await this.offlineStorage.getOrderDetail(tkt);
      if (detail) return [detail];
    }

    // 2. Offline only: fall back to list cache (partial fields, better than nothing)
    if (!online) {
      const list = await this.offlineStorage.getOrders();
      return list?.filter(o => o.tkt.toString() === tkt)
                  .map(o => ({ ...o, manualStatus: o.manualStatus ?? 0 })) ?? [];
    }
    
    // 3. Fetch from API
    const bgOpts = background
      ? { headers: this.gatewayOrderBackgroundHeaders() }
      : undefined;
    const raw = await this.viajesListGateway
      .post({ tkt_h: tkt }, bgOpts)
      .pipe(take(1))
      .toPromise();
    const orders = this.mapOrdersFromApi(toLegacyViajesEnvelope(raw));

    if (orders[0]) {
      await this.offlineStorage.saveOrderDetail(tkt, orders[0]).catch(() => null);
    }
    return orders;
  }

  setRejectValues(
    tkt: string,
    orderNumber: string,
    rejectReason: string,
    rejectDescription: string,
    files: File[],
    isMixer: boolean = true,
    rutClient: string = '',
    queuedFiles: SetValuesFileAttachment[] = []
  ) {
    return this.sendRejectValues(
      tkt,
      orderNumber,
      rejectReason,
      rejectDescription,
      files,
      isMixer,
      rutClient
    ).pipe(
      map(() => ({ status: 'sent' as const })),
      catchError(() =>
        from(
          this.offlineStorage.addOperationToQueue(tkt, {
            type: 'rejected',
            payload: {
              tkt,
              orderNumber,
              rejectReason,
              rejectDescription,
              rutClient,
              isMixer,
              files: queuedFiles,
            },
          })
        ).pipe(
          mergeMap(() => from(this.markOrderRejectedOffline(tkt))),
          map(() => ({ status: 'queued' as const }))
        )
      )
    );
  }

  changeDeliveryPoint(
    tkt: string,
    orderNumber: string,
    lat: number,
    lng: number
  ) {
    const headers = {
      Key: this.key,
      MX_TKT_H: tkt,
      MX_NUMERO_PEDID_H: orderNumber,
      MX_LATITUD_H: lat.toString(),
      MX_LONGITUD_H: lng.toString(),
    };
    return this.applySetValuesGateway(headers, undefined, undefined).pipe(
      map((data) => data)
    );
  }

  setExtraValues(
    tkt: string,
    orderNumber: string,
    waterAddition: boolean,
    anotherAddition: boolean,
    guidingRemarks: string,
    m3Returned: number,
    reasonToReturn: string,
    m3ReturnedPallet: number | string,
    files: File[],
    queuedFiles: SetValuesFileAttachment[] = []
  ) {
    return this.sendExtraValues(
      tkt,
      orderNumber,
      waterAddition,
      anotherAddition,
      guidingRemarks,
      m3Returned,
      reasonToReturn,
      m3ReturnedPallet,
      files
    ).pipe(
      map(() => ({ status: 'sent' as const })),
      catchError(() =>
        from(
          this.offlineStorage.addOperationToQueue(tkt, {
            type: 'extraValues',
            payload: {
              tkt,
              orderNumber,
              waterAddition,
              anotherAddition,
              guidingRemarks,
              m3Returned,
              reasonToReturn,
              m3ReturnedPallet,
              files: queuedFiles,
            },
          })
        ).pipe(map(() => ({ status: 'queued' as const })))
      )
    );
  }

  setWaterAddition(tkt: string, orderNumber: string, value: boolean) {
    return this.setExtraField(tkt, orderNumber, 'waterAddition', value.toString());
  }

  setAnotherAddition(tkt: string, orderNumber: string, value: boolean) {
    return this.setExtraField(tkt, orderNumber, 'anotherAddition', value.toString(), true);
  }

  setGuidingRemarks(
    tkt: string,
    orderNumber: string,
    remarks: string,
    showLoader = false
  ) {
    return this.setExtraField(tkt, orderNumber, 'guidingRemarks', remarks, showLoader);
  }

  setM3Returned(tkt: string, orderNumber: string, m3: number) {
    return this.setExtraField(tkt, orderNumber, 'm3Returned', m3.toString());
  }

  private setExtraField(
    tkt: string,
    orderNumber: string,
    field: ExtraField,
    value: string,
    showLoader = false
  ) {
    try {
      this.assertSetValuesIdentifiers(tkt, orderNumber, 'setExtraField');
    } catch (e) {
      return throwError(() => e);
    }
    return this.sendExtraField(tkt, orderNumber, field, value, showLoader).pipe(
      map(() => ({ status: 'sent' as const })),
      catchError(() =>
        from(
          this.offlineStorage.addOperationToQueue(tkt, {
            type: 'extraField',
            payload: { tkt, orderNumber, field, value },
          })
        ).pipe(map(() => ({ status: 'queued' as const })))
      )
    );
  }

  addOperatorComment(tkt: string, orderNumber: string, comment: string) {
    const headers = {
      Key: this.key,
      MX_TKT_H: tkt,
      MX_NUMERO_PEDID_H: orderNumber,
      MX_COMENTARIO: comment,
    };

    return this.applySetValuesGateway(headers, undefined, undefined).pipe(
      map((data) => data)
    );
  }

  updateStatus(tkt: string, orderNumber: string, status: number, hour?: string) {
    const currentHour = hour ?? moment().format('HH:mm:ss');
    return this.sendStatusUpdate(tkt, orderNumber, status, currentHour).pipe(
      catchError(() =>
        from(
          this.offlineStorage
            .addOperationToQueue(tkt, {
              type: 'statusUpdate',
              payload: { tkt, orderNumber, status, capturedHour: currentHour },
            })
            .then(() => this.patchCachedOrder(tkt, { manualStatus: status }))
            .then(() => ({}))
        )
      )
    );
  }

  getRejectReasons(): Observable<RejectReason[]> {
    return from(
      this.offlineStorage.getRejectReasons().then((cached) => {
        if (cached) return cached;
        if (!this.offlineStorage.isOnline()) return [] as RejectReason[];
        return this.motivosRechazoGateway
          .post()
          .pipe(
            take(1),
            map((raw) => normalizeGatewayRejectReasonsGrouped(raw) as unknown as RejectReason[])
          )
          .toPromise();
      })
    );
  }

  getReturnReasons() {
    return from(
      this.offlineStorage.getReturnReasons().then((cached) => {
        if (this.hasValidReferenceData(cached)) return cached as any;
        if (!this.offlineStorage.isOnline()) return [] as any;
        return this.motivosRetornoGateway
          .post()
          .pipe(
            map((raw) => {
              const rows = normalizeMotivosRetornoToLegacyRows(raw);
              return rows.map(
                (item) =>
                  ({
                    id: +Object.keys(item)[0],
                    description: Object.values(item)[0],
                  } as RejectReason)
              );
            }),
            take(1)
          )
          .toPromise();
      })
    );
  }

  setUnsignedGuide(tkt: string, orderNumber: string) {
    return this.sendUnsignedGuide(tkt, orderNumber).pipe(
      catchError(() =>
        from(
          this.offlineStorage
            .addOperationToQueue(tkt, {
              type: 'unsigned',
              payload: { tkt, orderNumber },
            })
            .then(() => this.patchCachedOrder(tkt, { isNotSigned: true }))
            .then(() => ({}))
        )
      )
    );
  }

  /** Envía el comentario de guía no firmada y lo encola si falla. */
  queueGuideComment(tkt: string, orderNumber: string, comment: string) {
    return this.setGuidingRemarks(tkt, orderNumber, comment);
  }

  /** Firma en papel (foto) — sigue usando POST /viajes/set-values. */
  setSignedGuide(
    tkt: string,
    orderNumber: string,
    rut: string,
    name: string,
    signature: string,
    email: string
  ) {
    return this.sendSignedGuide(
      tkt,
      orderNumber,
      rut,
      name,
      signature,
      email
    ).pipe(
      map(() => ({ status: 'sent' as const })),
      catchError(() => {
        this.eventLog.log('firma-digital', 'queue_paper', { tkt, orderNumber });
        return from(
          this.offlineStorage
            .addOperationToQueue(tkt, {
              type: 'signed',
              payload: { tkt, orderNumber, rut, name, signature, email },
            })
            .then(() =>
              this.patchCachedOrder(tkt, {
                isSignature: true,
                isNotSigned: false,
              })
            )
        ).pipe(map(() => ({ status: 'queued' as const })));
      })
    );
  }

  /** Firma digital (canvas) — POST /viajes/firma. */
  setSignedGuideCanvas(
    tkt: string,
    orderNumber: string,
    rut: string,
    name: string,
    signature: string,
    email: string,
    isAlreadySigned = false,
    tipoFirma?: ViajesFirmaTipo,
    comentario?: string,
    emailDos?: string
  ) {
    return this.viajesFirmaService.setSignedGuideCanvas(
      tkt,
      orderNumber,
      rut,
      name,
      signature,
      email,
      isAlreadySigned,
      tipoFirma,
      comentario,
      emailDos
    );
  }

  // ─── Direct HTTP methods used by CacheWarmupService only ────────────────────
  // Components must use getRejectReasons()/getReturnReasons() (cache-first).
  // These fall back to the API only when cache is missing or empty.

  fetchRejectReasonsDirect(): Observable<RejectReason[]> {
    return from(this.offlineStorage.getRejectReasons()).pipe(
      switchMap((cached) => {
        if (this.hasValidReferenceData(cached)) {
          return of(cached as RejectReason[]);
        }
        return this.motivosRechazoGateway
          .post({ headers: this.gatewayOrderBackgroundHeaders() })
          .pipe(map((raw) => normalizeGatewayRejectReasonsGrouped(raw) as any));
      })
    );
  }

  fetchReturnReasonsDirect() {
    return from(this.offlineStorage.getReturnReasons()).pipe(
      switchMap((cached) => {
        if (this.hasValidReferenceData(cached)) {
          return of(cached as RejectReason[]);
        }
        return this.motivosRetornoGateway
          .post({ headers: this.gatewayOrderBackgroundHeaders() })
          .pipe(
            map((raw) => {
              const rows = normalizeMotivosRetornoToLegacyRows(raw);
              return rows.map((item) => ({
                id: +Object.keys(item)[0],
                description: Object.values(item)[0],
              }));
            })
          );
      })
    );
  }

  private hasValidReferenceData(data: unknown): boolean {
    if (Array.isArray(data)) return data.length > 0;
    if (!data || typeof data !== 'object') return false;
    return Object.keys(data as Record<string, unknown>).length > 0;
  }

  // ─── Direct HTTP methods used by SyncService only ───────────────────────────
  // These bypass the queue and go straight to the API.
  // Components must NOT call these — use updateStatus/setExtraValues/setRejectValues/setUnsignedGuide/setSignedGuide/setSignedGuideCanvas/clearPendingOrder instead.

  sendStatusUpdate(tkt: string, orderNumber: string, status: number, capturedHour: string) {
    let base: Record<string, string>;
    if (status === 2) {
      base = { Key: this.key, MX_TKT_H: tkt, MX_NUMERO_PEDID_H: orderNumber, MX_ULTIMO_ESTADO_MANUAL_H: status.toString(), MX_HORA_DOS_H: capturedHour };
    } else if (status === 3) {
      base = { Key: this.key, MX_TKT_H: tkt, MX_NUMERO_PEDID_H: orderNumber, MX_ULTIMO_ESTADO_MANUAL_H: status.toString(), MX_HORA_TRES_H: capturedHour };
    } else if (status === 4) {
      base = { Key: this.key, MX_TKT_H: tkt, MX_NUMERO_PEDID_H: orderNumber, MX_ULTIMO_ESTADO_MANUAL_H: status.toString(), MX_HORA_CUATRO_H: capturedHour };
    } else if (status === 5) {
      base = { Key: this.key, MX_TKT_H: tkt, MX_NUMERO_PEDID_H: orderNumber, MX_ULTIMO_ESTADO_MANUAL_H: status.toString(), MX_HORA_CINCO_H: capturedHour };
    } else if (status === 6) {
      base = { Key: this.key, MX_TKT_H: tkt, MX_NUMERO_PEDID_H: orderNumber, MX_ULTIMO_ESTADO_MANUAL_H: status.toString(), MX_HORA_SEIS_H: capturedHour };
    } else {
      base = { Key: this.key, MX_TKT_H: tkt, MX_NUMERO_PEDID_H: orderNumber, MX_ULTIMO_ESTADO_MANUAL_H: String(status), MX_HORA_DOS_H: capturedHour };
    }
    const bgHeaders = this.gatewayOrderBackgroundHeaders();
    return this.applySetValuesGateway(base, undefined, { headers: bgHeaders }).pipe(map(d => d));
  }

  sendUnsignedGuide(tkt: string, orderNumber: string) {
    const base = { Key: this.key, MX_TKT_H: tkt, MX_NUMERO_PEDID_H: orderNumber, MX_CLIENTE_FIRMA_H: '0' };
    return this.applySetValuesGateway(base, undefined, { headers: this.gatewayOrderBackgroundHeaders() }).pipe(map(d => d));
  }

  sendSignedGuide(tkt: string, orderNumber: string, rut: string, name: string, signature: string, email: string) {
    const base = {
      Key: this.key,
      MX_TKT_H: tkt,
      MX_NUMERO_PEDID_H: orderNumber,
      MX_CLI_FIRMA_RUT: rut,
      MX_CLI_FIRMA_NOMBRE: name,
      MX_CLI_FIRMA: signature,
      MX_CLI_FIRMA_CORREO: email,
      MX_CLIENTE_FIRMA_H: '1',
    };
    return this.applySetValuesGateway(
      base,
      undefined,
      { headers: new HttpHeaders().set('X-Background-Request', '1') }
    ).pipe(map(d => d));
  }

  sendSignedGuideCanvas(
    tkt: string,
    orderNumber: string,
    rut: string,
    name: string,
    signature: string,
    email: string,
    tipoFirma?: ViajesFirmaTipo,
    comentario?: string,
    emailDos?: string
  ) {
    return this.viajesFirmaService.sendSignedGuideCanvas(
      tkt,
      orderNumber,
      rut,
      name,
      signature,
      email,
      tipoFirma,
      comentario,
      emailDos
    );
  }

  sendRejectValues(
    tkt: string,
    orderNumber: string,
    rejectReason: string,
    rejectDescription: string,
    files: File[],
    isMixer: boolean,
    rutClient: string,
    queuedFiles: SetValuesFileAttachment[] = []
  ) {
    try {
      this.assertSetValuesIdentifiers(tkt, orderNumber, 'sendRejectValues');
    } catch (e) {
      return throwError(() => e);
    }
    const base = {
      Key: this.key,
      MX_TKT_H: tkt,
      MX_NUMERO_PEDID_H: orderNumber,
      MX_RECHAZO_FLAG: '1',
      MX_MOTIVO_RECHAZO: rejectReason,
      MX_DESCRIPCION_RECHAZO: rejectDescription,
      MX_RECHAZO_RUT_CLI: rutClient,
    };
    return this.applySetValuesGateway(
      base,
      files,
      {
        headers: new HttpHeaders().set('X-Background-Request', '1'),
      },
      !isMixer,
      queuedFiles
    ).pipe(map(d => d));
  }

  sendExtraValues(
    tkt: string,
    orderNumber: string,
    waterAddition: boolean,
    anotherAddition: boolean,
    guidingRemarks: string,
    m3Returned: number,
    reasonToReturn: string,
    m3ReturnedPallet: number | string,
    files: File[],
    queuedFiles: SetValuesFileAttachment[] = []
  ) {
    const base = {
      Key: this.key,
      MX_TKT_H: tkt,
      MX_NUMERO_PEDID_H: orderNumber,
      MX_ADICIONAL_UNO_H: waterAddition.toString(),
      MX_ADICIONAL_DOS_H: anotherAddition.toString(),
      MX_ADICIONAL_TRES_H: guidingRemarks,
      MX_M3_RETORNO: m3Returned.toString(),
      MX_MOTIVO_RETORNO: reasonToReturn,
      MX_PALLET_RETORNO: m3ReturnedPallet.toString(),
      ES_GUIA_DIGITAL: '1',
    };
    return this.applySetValuesGateway(
      base,
      files,
      { headers: this.gatewayOrderBackgroundHeaders() },
      false,
      queuedFiles
    ).pipe(map(d => d));
  }

  sendExtraField(
    tkt: string,
    orderNumber: string,
    field: ExtraField,
    value: string,
    showLoader = false
  ) {
    const headers: Record<string, string> = {
      Key: this.key,
      MX_TKT_H: tkt,
      MX_NUMERO_PEDID_H: orderNumber,
      [EXTRA_FIELD_HEADER[field]]: value,
    };
    const httpHeaders = showLoader
      ? new HttpHeaders().set('X-Background-Request', '1')
      : this.gatewayOrderBackgroundHeaders();
    return this.applySetValuesGateway(headers, undefined, {
      headers: httpHeaders,
    });
  }

  /** Patches the cached order and orders list to mark an order as rejected offline. */
  async markOrderRejectedOffline(tkt: string): Promise<void> {
    await this.patchCachedOrder(tkt, { rejectionFlag: '1' });
  }

  /** Updates specific fields on the cached order entry and orders list. */
  private async patchCachedOrder(tkt: string, patch: Partial<Order>): Promise<void> {
    this.markRecentlyMutated(tkt);
    const detail = await this.offlineStorage.getOrderDetail(tkt);
    if (detail) {
      await this.offlineStorage.saveOrderDetail(tkt, { ...detail, ...patch });
    }
    const list = await this.offlineStorage.getOrders();
    if (list) {
      await this.offlineStorage.saveOrders(
        list.map(o => o.tkt.toString() === tkt ? { ...o, ...patch } : o)
      );
    }
  }

  getOnlyNumbers(text: string) {
    return text.replace(/\D/g, '');
  }

  setMessageSos(
    message: string
  ) {
    const headers = {
      Key: this.key,
      MX_COMENTARIO_SOS: message,
    };
    return this.applySetValuesGateway(headers, undefined, undefined);
  }

  setChangeFlagMessage() {
    const headers = {
      Key: this.key,
      MX_COMENTARIO_SOS_NO_LEIDO: '0',
    };
    return this.applySetValuesGateway(headers, undefined, undefined);
  }

  /**
   * Quita el despacho de "Otros despachos".
   * Solo debe llamarse al finalizar: extras, rechazo y firma no tocan este flag.
   */
  clearPendingOrder(
    tkt: string,
    orderNumber: string,
    options?: { commentsFinal?: string; finFlujo?: boolean }
  ) {
    try {
      this.assertSetValuesIdentifiers(tkt, orderNumber, 'clearPendingOrder');
    } catch (e) {
      return throwError(() => e);
    }
    const commentsFinal = options?.commentsFinal ?? '';
    const finFlujo = !!options?.finFlujo;
    return from(this.patchCachedOrder(tkt, { flag_pending: '0' })).pipe(
      switchMap(() =>
        this.sendClearPendingOrder(tkt, orderNumber, options).pipe(
          map(() => ({ status: 'sent' as const })),
          catchError(() =>
            from(
              this.offlineStorage.addOperationToQueue(tkt, {
                type: 'clearPending',
                payload: { tkt, orderNumber, commentsFinal, finFlujo },
              })
            ).pipe(map(() => ({ status: 'queued' as const })))
          )
        )
      )
    );
  }

  sendClearPendingOrder(
    tkt: string,
    orderNumber: string,
    options?: { commentsFinal?: string; finFlujo?: boolean }
  ) {
    const headers: Record<string, string> = {
      Key: this.key,
      MX_TKT_H: tkt,
      MX_NUMERO_PEDID_H: orderNumber,
      FLAG_PEDIDO_PEN: '0',
    };
    if (options?.finFlujo) {
      headers.FIN_FLUJO_H = '1';
    }
    if (options?.commentsFinal != null) {
      headers.MX_COMENTARIOS_FINAL = options.commentsFinal;
    }
    return this.applySetValuesGateway(headers, undefined, {
      headers: this.gatewayOrderBackgroundHeaders(),
    }).pipe(map((data) => data));
  }

  setPendingOrder(tkt: string, orderNumber: string) {
    try {
      this.assertSetValuesIdentifiers(tkt, orderNumber, 'setPendingOrder');
    } catch (e) {
      return throwError(() => e);
    }
    const headers = {
      Key: this.key,
      MX_TKT_H: tkt,
      FLAG_PEDIDO_PEN: '1',
      MX_NUMERO_PEDID_H: orderNumber,
    };
    return from(this.patchCachedOrder(tkt, { flag_pending: '1' })).pipe(
      switchMap(() =>
        this.applySetValuesGateway(headers, undefined, undefined).pipe(
          map((data) => data)
        )
      )
    );
  }

  setRegenerateQr(tkt: string, orderNumber: string) {
    const headers = {
      Key: this.key,
      MX_TKT_H: tkt,
      ES_GUIA_DIGITAL: '1',
      MX_NUMERO_PEDID_H: orderNumber,
    };
    return this.applySetValuesGateway(headers, undefined, undefined).pipe(
      map((data) => data)
    );
  }

  getResumenDespacho(
    desde: string,
    hasta: string,
    _skipLoader = false
  ): Observable<any> {
    return this.despachosResumenGateway
      .post({ desde, hasta })
      .pipe(map((raw) => toLegacyResumenDespachoArray(raw)));
  }

}
